import React, { useState, useEffect, useRef } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";
import { useAuth } from "../context/AuthContext";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface BloodRecordsPageProps {
  onBack: () => void;
}

export const BloodRecordsPage: React.FC<BloodRecordsPageProps> = ({ onBack }) => {
  const { loadHealthByDate } = useAuth();
  const [bloodData, setBloodData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDateData, setSelectedDateData] = useState<any>(null);
  const [selectedDateLoading, setSelectedDateLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  // 초기 로드: 첫 10일 데이터
  useEffect(() => {
    const loadBloodHistory = async () => {
      setLoading(true);
      try {
        const today = new Date();
        const bloodDataArray: any[] = [];
        const limit = 10;

        for (let i = 0; i < limit; i++) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];

          const healthData = await loadHealthByDate(dateStr);
          const hasBloodPressure = healthData?.bloodPressure && healthData.bloodPressure.length > 0;
          const hasBloodGlucose = healthData?.bloodGlucose && healthData.bloodGlucose.length > 0;

          if (hasBloodPressure || hasBloodGlucose) {
            bloodDataArray.push({
              date: dateStr,
              bloodPressure: healthData?.bloodPressure || [],
              bloodGlucose: healthData?.bloodGlucose || [],
            });
          }
        }

        setBloodData(bloodDataArray);
        setHasMore(bloodDataArray.length >= limit);
      } catch (error) {
        console.error('[BloodHistory] Error loading data:', error);
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadBloodHistory();
  }, [loadHealthByDate]);

  // IntersectionObserver: 스크롤 감지로 추가 데이터 로드
  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !isInitialLoad) {
          setLoadingMore(true);
          try {
            const today = new Date();
            const newBloodDataArray: any[] = [];
            const limit = 10;
            const newOffset = offset + limit;

            for (let i = newOffset; i < newOffset + limit; i++) {
              const date = new Date(today);
              date.setDate(date.getDate() - i);
              const dateStr = date.toISOString().split('T')[0];

              const healthData = await loadHealthByDate(dateStr);
              const hasBloodPressure = healthData?.bloodPressure && healthData.bloodPressure.length > 0;
              const hasBloodGlucose = healthData?.bloodGlucose && healthData.bloodGlucose.length > 0;

              if (hasBloodPressure || hasBloodGlucose) {
                newBloodDataArray.push({
                  date: dateStr,
                  bloodPressure: healthData?.bloodPressure || [],
                  bloodGlucose: healthData?.bloodGlucose || [],
                });
              }
            }

            setBloodData((prev) => [...prev, ...newBloodDataArray]);
            setOffset(newOffset);
            setHasMore(newBloodDataArray.length >= limit);
          } catch (error) {
            console.error('[BloodHistory] Error loading more data:', error);
          } finally {
            setLoadingMore(false);
          }
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [offset, hasMore, loadingMore, isInitialLoad, loadHealthByDate]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dayLabels = ["일", "월", "화", "수", "목", "금", "토"];
    const dayLabel = dayLabels[date.getDay()];
    return `${year}년 ${month}월 ${day}일 (${dayLabel})`;
  };

  const getBloodPressureStatus = (systolic: number) => {
    if (systolic < 120) return { label: "정상", color: "emerald" };
    if (systolic < 130) return { label: "높음", color: "amber" };
    return { label: "매우높음", color: "red" };
  };

  const getBloodGlucoseStatus = (glucose: number) => {
    if (glucose < 100) return { label: "정상", color: "emerald" };
    if (glucose < 126) return { label: "높음", color: "amber" };
    return { label: "매우높음", color: "red" };
  };

  // 날짜 클릭 시 상세 데이터 로드
  const handleDateClick = async (dateStr: string) => {
    setSelectedDateLoading(true);
    try {
      const data = await loadHealthByDate(dateStr);
      setSelectedDateData(data);
      setSelectedDate(dateStr);
    } catch (error) {
      console.error('[BloodHistory] Error loading date data:', error);
    } finally {
      setSelectedDateLoading(false);
    }
  };

  // 날짜별 상세보기 페이지
  if (selectedDate && selectedDateData) {
    const hasBloodPressure = selectedDateData?.bloodPressure && selectedDateData.bloodPressure.length > 0;
    const hasBloodGlucose = selectedDateData?.bloodGlucose && selectedDateData.bloodGlucose.length > 0;
    
    return (
      <SimpleModulePage
        title="혈당&혈압 기록"
        subtitle={formatDate(selectedDate)}
        icon="🩺"
        onBack={() => setSelectedDate(null)}
      >
        {/* 혈압 데이터 */}
        <SectionCard>
          <p className="text-[13px] font-semibold text-slate-900 mb-3">혈압 (Blood Pressure)</p>
          {hasBloodPressure ? (
            <div className="space-y-2">
              {selectedDateData.bloodPressure.map((bp: any, idx: number) => {
                const status = getBloodPressureStatus(bp.systolic);
                return (
                  <div key={idx} className="p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-[14px] font-bold text-slate-900">
                          {bp.systolic} / {bp.diastolic} mmHg
                        </p>
                        {bp.pulse && (
                          <p className="text-[11px] text-slate-600">맥박: {bp.pulse} bpm</p>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-semibold bg-${status.color}-100 text-${status.color}-700`}>
                        {status.label}
                      </span>
                    </div>
                    {bp.measurement_time && (
                      <p className="text-[10px] text-slate-500">
                        {new Date(bp.measurement_time).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-3 border border-slate-200 rounded-lg bg-slate-50">
              <p className="text-[13px] text-slate-600 text-center">미기록</p>
            </div>
          )}
        </SectionCard>

        {/* 혈당 데이터 */}
        <SectionCard>
          <p className="text-[13px] font-semibold text-slate-900 mb-3">혈당 (Blood Glucose)</p>
          {hasBloodGlucose ? (
            <div className="space-y-2">
              {selectedDateData.bloodGlucose.map((bg: any, idx: number) => {
                const status = getBloodGlucoseStatus(bg.glucose_level);
                return (
                  <div key={idx} className="p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-[14px] font-bold text-slate-900">
                          {bg.glucose_level} mg/dL
                        </p>
                        {bg.meal_relation && (
                          <p className="text-[11px] text-slate-600">
                            {bg.meal_relation === 'fasting' && '공복'}
                            {bg.meal_relation === 'after_meal' && '식후 2시간'}
                            {bg.meal_relation === 'before_meal' && '식전'}
                            {bg.meal_relation === 'random' && '임의 측정'}
                          </p>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-semibold bg-${status.color}-100 text-${status.color}-700`}>
                        {status.label}
                      </span>
                    </div>
                    {bg.measurement_time && (
                      <p className="text-[10px] text-slate-500">
                        {new Date(bg.measurement_time).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-3 border border-slate-200 rounded-lg bg-slate-50">
              <p className="text-[13px] text-slate-600 text-center">미기록</p>
            </div>
          )}
        </SectionCard>
      </SimpleModulePage>
    );
  }

  return (
    <SimpleModulePage
      title="혈당&혈압"
      subtitle="측정 기록"
      icon="🩺"
      onBack={onBack}
    >
      {loading ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">로딩 중...</p>
        </SectionCard>
      ) : bloodData.length === 0 ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">
            혈당&혈압 기록이 없습니다.
          </p>
        </SectionCard>
      ) : (
        <SectionCard>
          <div className="space-y-0">
            {bloodData.map((blood, idx) => (
              <button
                key={blood.date}
                onClick={() => handleDateClick(blood.date)}
                disabled={selectedDateLoading}
                className={`w-full py-3 px-0 text-left border-b border-slate-100 ${
                  idx === bloodData.length - 1 ? 'last:border-b-0' : ''
                } hover:bg-slate-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div>
                  <p className="text-[13px] font-semibold text-slate-900 mb-1">
                    {formatDate(blood.date)}
                  </p>
                  <p className="text-[11px] text-slate-600">
                    {blood.bloodPressure.length > 0 && `혈압 ${blood.bloodPressure.length}회`}
                    {blood.bloodPressure.length > 0 && blood.bloodGlucose.length > 0 && ' · '}
                    {blood.bloodGlucose.length > 0 && `혈당 ${blood.bloodGlucose.length}회`}
                  </p>
                </div>
                <span className="text-slate-400">→</span>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {/* 페이지네이션: 스크롤 감지 대상 */}
      {hasMore && (
        <div ref={observerTarget} className="w-full py-4 flex justify-center">
          {loadingMore && (
            <p className="text-[12px] text-slate-500">추가 데이터 로드 중...</p>
          )}
        </div>
      )}

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">정보 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              혈당 및 혈압 측정 기록을 확인할 수 있습니다. 각 날짜를 클릭하여 상세 정보를 확인하세요.
            </p>
          </div>
        </div>
      </SectionCard>
    </SimpleModulePage>
  );
};
